import { useMemo, useState } from "react";
import { Check, KeyRound } from "lucide-react";
import { buildDealJournal, taskLabel } from "@/utils/deals-view.utils";
import { useLiveCountdown } from "@/hooks/useLiveCountdown";
import { formatUtcDate, parseIsoDate } from "@/utils/common.utils";
import type { ClientCalmDeal, ClientSummaryResponse, ExternalFocusedDeal } from "@/types/deals.types";

type JournalEntryData = ReturnType<typeof buildDealJournal>[number];

/**
 * The Journey timeline — moved here (from `ClientDealView/ClientDealJourney.tsx`)
 * so every portal can share one implementation. `deal.contract_terms` /
 * `under_contract_at` / `phase` are on both `client_calm` and
 * `external_focused` payloads, so no data is missing for a lender or attorney;
 * only the `voice` differs — `buildDealJournal` swaps a handful of
 * second-person sentences ("I'll flag anything you need to weigh in on") for
 * neutral third person when `voice="external"`, since those lines are the
 * agent speaking to *the client specifically* and read wrong to anyone else.
 */
interface JourneyProps {
  deal: ClientCalmDeal | ExternalFocusedDeal;
  summary?: ClientSummaryResponse | null;
  /** The next unmarked checklist items, already sliced. The journey advances
   *  on its own as the agent ticks these off. */
  upcoming?: Array<Record<string, any>>;
  /** The checklist items most recently ticked off — the steps already behind. */
  completed?: Array<Record<string, any>>;
  /** "client" (default) keeps the agent's first-person voice; "external" reads
   *  correctly for a lender, attorney, co-agent, or vendor. */
  voice?: "client" | "external";
  /** 2.3: collapse the milestones already behind the deal under a "View earlier"
   *  link. On for the client's main page; off for the collaborator portal, which
   *  wants the full read-only timeline. */
  collapsePast?: boolean;
  testId?: string;
}

export function Journey({ deal, upcoming = [], completed = [], voice = "client", collapsePast = false, testId = "client-journey" }: JourneyProps) {
  const [showAll, setShowAll] = useState(false);
  const entries = useMemo(
    () => buildDealJournal(deal, { upcoming, completed, voice }),
    [deal, upcoming, completed, voice],
  );
  if (entries.length === 0) return null;

  // 2.3: the main page leads with the present milestone; everything already
  // behind you collapses under "View earlier milestones".
  const presentIdx = entries.findIndex((e) => e.tone === "present");
  const firstShown = collapsePast && presentIdx > 0 ? presentIdx : 0;
  const hidden = firstShown;
  const shown = showAll ? entries : entries.slice(firstShown);

  return (
    <div className="mt-4 animate-in fade-in slide-in-from-bottom-4 duration-500 delay-300 ease-out" data-testid={testId}>
      <div className="mb-5 text-[12px] font-bold uppercase tracking-[0.32em] text-dash-ink">The Journey</div>
      {hidden > 0 && !showAll && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mb-4 text-[12px] font-medium text-dash-muted underline underline-offset-4 hover:text-dash-ink"
          data-testid="journey-show-earlier"
        >
          View {hidden} earlier milestone{hidden === 1 ? "" : "s"}
        </button>
      )}
      <div className="flex flex-col">
        {shown.map((e, i) => (
          <JournalEntry key={e.key} entry={e} deal={deal} isLast={i === shown.length - 1} />
        ))}
      </div>
    </div>
  );
}

interface JournalEntryProps {
  entry: JournalEntryData;
  deal: ClientCalmDeal | ExternalFocusedDeal;
  isLast: boolean;
}

function JournalEntry({ entry, deal, isLast }: JournalEntryProps) {
  const isPast = entry.tone === "past";
  const isPresent = entry.tone === "present";
  const isClose = entry.key === "close";
  const dotFillCls = isPresent ? "bg-dash-limeDark" : isPast ? "bg-dash-chip" : "bg-white border border-dash-border";
  const closingDate = isClose
    ? parseIsoDate(deal.contract_terms?.closing_datetime) || parseIsoDate(deal.contract_terms?.closing_date, true)
    : null;
  const closingHasTime = Boolean(isClose && deal.contract_terms?.closing_datetime);

  return (
    <div className="flex w-full gap-6" data-testid={`journey-${entry.key}`}>
      <div className="flex w-8 shrink-0 flex-col items-center">
        <span
          className={`flex size-7 shrink-0 items-center justify-center rounded-full ${dotFillCls}`}
          style={isPresent ? { boxShadow: "0 0 0 4px var(--dash-lime-glow)" } : undefined}
          aria-hidden
        >
          {isPast && <Check className="size-4 text-dash-muted" strokeWidth={2.4} />}
        </span>
        {!isLast && <span className="mt-1 min-h-20 w-px flex-1 bg-dash-border" aria-hidden />}
      </div>
      <div className="min-w-0 flex-1 pb-8">
        <div className="mb-1 text-[11px] font-bold uppercase tracking-[0.22em] text-dash-faint">
          {isPresent ? "Right now" : entry.dateLabel}
        </div>
        <div className="font-display text-[24px] font-semibold leading-tight tracking-normal text-dash-ink">{entry.title}</div>
        {entry.body && (
          <p className="mt-2 max-w-[520px] text-[14px] leading-6 text-dash-muted">{entry.body}</p>
        )}
        {/* Live mirror of the agent's checklist — what is genuinely next (or
            just done), rather than a sentence written months ago. */}
        {Array.isArray(entry.items) && entry.items.length > 0 && (
          <ul
            className="mt-3 flex max-w-[560px] flex-col gap-2"
            data-testid={isPast ? "journey-completed" : "journey-upcoming"}
          >
            {entry.items.map((item: Record<string, any>) => (
              <li key={item.id} className="flex items-start gap-2.5 text-[13px] leading-[1.54] text-dash-muted">
                {isPast
                  ? <Check className="mt-[3px] size-3.5 shrink-0 text-dash-limeDark" strokeWidth={2.6} aria-hidden />
                  : <span className="mt-[6px] size-1.5 shrink-0 rounded-full bg-dash-border" aria-hidden />}
                <span className="min-w-0">
                  {taskLabel(item)}
                  {!isPast && item.due_date && (
                    <span className="ml-1.5 text-[11px] text-dash-faint">
                      · {formatUtcDate(item.due_date)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {isClose && closingDate && (
          <ClosingCard date={closingDate} body={entry.body} showTime={closingHasTime} />
        )}
      </div>
    </div>
  );
}

function ClosingCard({ date, body, showTime }: { date: Date; body?: string | null; showTime: boolean }) {
  const parts = useLiveCountdown(date);
  const dateLabel = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  const timeLabel = showTime ? date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "Time TBD";
  const values: Array<[number, string]> = parts
    ? [
        [parts.days, "Days"],
        [parts.hours, "Hrs"],
        [parts.minutes, "Mins"],
        [parts.seconds, "Secs"],
      ]
    : [];

  return (
    <div className="mt-5 overflow-hidden rounded-[8px] border border-dash-border bg-white shadow-[0_10px_25px_rgba(32,35,31,0.08)] md:flex" data-testid="journey-closing-card">
      <div className="bg-dash-greenDeep p-5 text-white md:w-[185px]">
        <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-white/70">Next up</div>
        <div className="mt-4 font-display text-[28px] font-semibold leading-none tracking-normal">{dateLabel}</div>
        <div className="mt-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-white/65">{timeLabel}</div>
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-between gap-5 p-5 md:p-6">
        <div className="min-w-0">
          <div className="font-display text-[30px] font-semibold leading-none tracking-normal text-dash-ink">Closing day</div>
          {body && <p className="mt-2 max-w-[460px] text-[13px] leading-5 text-dash-muted">{body}</p>}
          {parts && !parts.expired && (
            <div className="mt-5 grid max-w-[360px] grid-cols-4 divide-x divide-dash-border">
              {values.map(([value, label]) => (
                <div key={label} className="min-w-0 px-3 first:pl-0">
                  <div className="font-display text-[24px] font-semibold leading-none tracking-normal text-dash-ink">
                    {label === "Days" ? value : String(value).padStart(2, "0")}
                  </div>
                  <div className="mt-1 text-[9px] font-bold uppercase tracking-[0.12em] text-dash-muted">{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="hidden size-16 shrink-0 items-center justify-center rounded-full border-2 border-dash-greenDeep/30 text-dash-greenDeep sm:flex">
          <KeyRound className="size-7" />
        </div>
      </div>
    </div>
  );
}
